<template>
    <div class="modal fade show hotel-modal" aria-labelledby="billAdjustModalTitle">
        <div class="modal-dialog modal-dialog-centered modal-xl">
            <div class="modal-content">
                <div class="d-flex justify-content-between align-items-center gap-1 px-1 my-1">
                    <h1 class="modal-heading mb-0">Bill Adjust</h1>
                    <div class="d-flex align-items-center gap-1 flex-shrink-0">
                        <button @click="onClose" type="button" class="btn-close" aria-label="Close"></button>
                    </div>
                </div>

                <hr class="mt-0">

                <div class="modal-body">
                    <div class="table-responsive">
                        <table class="table table-sm table-bordered detail-table mb-0">
                            <thead>
                                <tr>
                                    <th>Room</th>
                                    <th class="text-end">Previous Night</th>
                                    <th class="text-end">New Night</th>
                                    <th class="text-end">Rate</th>
                                    <th class="text-end">Existing Bill</th>
                                    <th class="text-end">New Calculated Amount</th>
                                    <th class="text-end">Adjustment Amount</th>
                                    <th>Adjustment Type</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in previews" :key="row.service_resource_id">
                                    <td>{{ textValue(row.room_name) }}</td>
                                    <td class="text-end">{{ textValue(row.previous_nights) }}</td>
                                    <td class="text-end">{{ textValue(row.new_nights) }}</td>
                                    <td class="text-end">{{ commaFormat(row.rate) }}</td>
                                    <td class="text-end">{{ commaFormat(row.existing_amount) }}</td>
                                    <td class="text-end">{{ commaFormat(row.new_amount) }}</td>
                                    <td class="text-end">{{ commaFormat(row.adjustment_amount) }}</td>
                                    <td>
                                        <span class="badge" :class="adjustmentBadgeClass(row)">{{ adjustmentTypeLabel(row) }}</span>
                                    </td>
                                    <td class="text-nowrap">
                                        <div v-if="isAdditionalBill(row)" class="d-flex gap-1">
                                            <button
                                                type="button"
                                                class="btn btn-sm btn-outline-danger"
                                                :disabled="isRoomLoading(row)"
                                                @click="onOnlyBill(row)"
                                                v-if="false"
                                            >
                                                Only Bill
                                            </button>
                                            <button
                                                type="button"
                                                class="btn btn-sm btn-danger"
                                                :disabled="isRoomLoading(row)"
                                                @click="onBillWithReceipt(row)"
                                            >
                                                Bill + Receipt
                                            </button>
                                        </div>
                                        <button
                                            v-else
                                            type="button"
                                            class="btn btn-sm btn-danger"
                                            :disabled="isRoomLoading(row)"
                                            @click="onConfirmRoom(row)"
                                        >
                                            Confirm
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div class="d-flex flex-wrap justify-content-end gap-1 mt-2 px-2 pb-2">
                    <button type="button" class="btn btn-outline-secondary flex-grow-1 flex-sm-grow-0" @click="onClose">Close</button>
                </div>
            </div>
        </div>

        <BillAdjustReceiptModal
            v-if="receiptRow"
            :row="receiptRow"
            :loading="isRoomLoading(receiptRow)"
            @onClose="receiptRow = null"
            @onSubmit="onReceiptSubmit"
        />
    </div>
</template>

<script setup>
import { inject, ref } from 'vue';
import { useRoute } from 'vue-router';
import handleHotel from '@/services/modules/hotel/hotel';
import figureFormatter from '@/services/utils/figureFormatter';
import BillAdjustReceiptModal from '@/components/molecule/company/hotel/hotel-reservation/BillAdjustReceiptModal.vue';

const props = defineProps({
    previews: {
        type: Array,
        required: true,
    },
    reservationId: {
        type: [Number, String],
        required: true,
    },
});

const emit = defineEmits(['onClose', 'onConfirmed']);

const $route = useRoute();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const { confirmHotelBillAdjust } = handleHotel();
const { commaFormat } = figureFormatter();

const loadingRoomIds = ref([]);
const receiptRow = ref(null);

const textValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return value;
};

const adjustmentTypeLabel = (row) => {
    if (row.adjustment_type === 'bill_return') return 'Bill Return';
    return 'Additional Bill';
};

const adjustmentBadgeClass = (row) => {
    if (row.adjustment_type === 'bill_return') return 'bg-light-danger';
    return 'bg-light-success';
};

const isRoomLoading = (row) => {
    return loadingRoomIds.value.includes(row.service_resource_id);
};

const onClose = () => {
    emit('onClose');
};

const isAdditionalBill = (row) => {
    return row.adjustment_type !== 'bill_return';
};

const onConfirmRoom = (row) => {
    if (isRoomLoading(row)) return;
    if (!confirm(`Are you sure you want to return ${commaFormat(Math.abs(row.adjustment_amount))} (${row.previous_nights - row.new_nights} night(s)) for ${row.room_name}?`)) return;
    submitConfirm(row, { company_id: $route.params.companyId });
};

const onOnlyBill = (row) => {
    if (isRoomLoading(row)) return;
    if (!confirm(`Are you sure you want to confirm the additional bill of ${commaFormat(row.adjustment_amount)} for ${row.room_name} without a money receipt?`)) return;
    submitConfirm(row, { company_id: $route.params.companyId });
};

const onBillWithReceipt = (row) => {
    if (isRoomLoading(row)) return;
    receiptRow.value = row;
};

const onReceiptSubmit = (receiptData) => {
    const payload = Object.assign({}, receiptData, {
        company_id: $route.params.companyId,
        payment_mode: 'receipt',
    });
    submitConfirm(receiptRow.value, payload);
};

const submitConfirm = (row, payload) => {
    loadingRoomIds.value = loadingRoomIds.value.concat([row.service_resource_id]);
    confirmHotelBillAdjust(payload, props.reservationId, row.service_resource_id).then(res => {
        if (!res.status) {
            showError(res.message);
            return;
        }
        showSuccess(res.message);
        receiptRow.value = null;
        emit('onConfirmed');
    }).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        loadingRoomIds.value = loadingRoomIds.value.filter(id => id !== row.service_resource_id);
    });
};
</script>

<style scoped>
.hotel-modal {
    display: block;
    background: #1c1d1c33;
}
.modal-heading {
    font-size: 1.5rem;
}
.detail-table th {
    background: #f8f8f8;
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #6e6b7b;
    white-space: nowrap;
}
.detail-table td {
    font-size: 0.9rem;
    font-weight: 600;
    color: #384250;
}
</style>
